<template>
  <PortalLayout section="Prepare" hidePortalSelector hideHeader>
    <div class="pb-4">
      <BackButton :to="'../trainingprogram/' + trainingModule.trainingProgramId">
        Back to Training Program
      </BackButton>
    </div>
    <div v-if="isPropertyLoading || isTrainingModuleLoading"><Loading /></div>
    <div v-else>
      <h1 class="py-3 mb-6 border-b flex">
        <div class="text-xl flex-grow">
          <div class="font-bold">Edit Training Module</div>
          <div>
            {{ trainingModule?.name }}
            <span v-if="!trainingModule?.isActive">(inactive)</span>
          </div>
        </div>
        <EditButton @click="editModuleClick">Edit</EditButton>
      </h1>
      <h2 class="font-medium">Tests</h2>
      <div v-if="moduleTests && moduleTests.length > 0">
        <AddButton class="mt-2" @click="addTestClick">Add Test</AddButton>
        <div
          v-for="moduleTest in moduleTests"
          :key="moduleTest.testId"
          class="p-4 my-2 border-t"
          :class="moduleTest.isActive ? 'bg-gray-100' : 'bg-gray-400'"
        >
          <div class="flex">
            <div
              class="py-1 px-2 hover:bg-blue-200 flex-grow rounded cursor-pointer"
              @click="testClick(moduleTest)"
            >
              <div>
                {{ moduleTest.title }} <span v-if="!moduleTest.isActive">(inactive)</span>
              </div>
            </div>
            <EditButton @click="editTestClick(moduleTest)" iconOnly="true" />
          </div>
        </div>
      </div>
      <div class="m-2" v-else>
        <span>-- No Tests found for this Training Module --</span>
      </div>
      <AddButton @click="addTestClick">Add Test</AddButton>
      <br /><br />
      <h2 class="font-medium">Video</h2>
      <div v-if="trainingModule.videoId">
        <p>
          {{ moduleVideo?.fileName }}
          <span class="text-gray-500">({{ moduleVideo?.label }})</span>
        </p>
      </div>
      <div v-else>
        <p>None selected.</p>
      </div>
      <br />
      <EditPopup
        entity-name="Test"
        :is-visible="isTestPopupVisible"
        :is-edit-mode="isTestEditing"
        :save-error="saveError"
        :delete-enabled="true"
        @close="isTestPopupVisible = false"
        @save="saveTestClick"
        @delete="deleteTestClick"
      >
        <BaseInput label="Name" v-model="testTitle" :error="errorsTest.testTitle" />
        <div class="h-3" />
        <BaseCheckbox label="Currently Active" v-model="testIsActive" />
      </EditPopup>
      <EditPopup
        entity-name="Training Module"
        :is-visible="isModulePopupVisible"
        :is-edit-mode="true"
        :save-error="saveError"
        :delete-enabled="false"
        @close="isModulePopupVisible = false"
        @save="saveModuleClick"
      >
        <BaseInput label="Name" v-model="moduleName" :error="errorsModule.moduleName" />
        <div class="h-3" />
        <BaseCheckbox label="Currently Active" v-model="moduleIsActive" /><br />
        <BaseSelect
          label="Video"
          :options="videoOptions"
          v-model="moduleVideoId"
          :default-option="defaultVideoOption"
          :error="errorsModule.moduleVideoId"
        />
      </EditPopup>
    </div>
  </PortalLayout>
</template>

<script setup lang="ts">
import { useBldgStore } from "../../store/bldgStore";
import { useTrainingStore } from "../../store/trainingStore";
import { useAdminStore } from "../../store/adminStore";
import { TestDTO, TrainingModuleDTO } from "../../store/generatedFetchClient";

import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { NameValue } from "../../sharedTypes";

const bldgStore = useBldgStore();
const trainingStore = useTrainingStore();
const adminStore = useAdminStore();
const route = useRoute();
const router = useRouter();

var isPropertyLoading = computed(() => bldgStore.isLoading);
var isTrainingModuleLoading = ref(true);
var property = computed(() => bldgStore.property);
let videos = computed(() => adminStore.videos);

let trainingModule = computed(() => trainingStore.selectedTrainingModule);
let moduleTests = computed(() => trainingStore.selectedModuleTests);

let moduleVideo = computed(() =>
  videos.value.find((v) => v.videoId === trainingModule.value.videoId)
);

let testClick = function (program: TestDTO) {
  router.push(`../test/${program.testId}`);
};

onMounted(loadData);
watch(property, loadData);
async function loadData() {
  isTrainingModuleLoading.value = true;
  if (property.value?.propertyId) {
    if (
      route.params.trainingModuleId &&
      typeof route.params.trainingModuleId === "string"
    ) {
      let trainingModuleId = Number.parseInt(route.params.trainingModuleId);
      await trainingStore.getTrainingModule(trainingModuleId);
      await trainingStore.getTestsByTrainingModule(trainingModuleId);
      await adminStore.getVideosList();
    }
  }
  isTrainingModuleLoading.value = false;
}

// Popup stuff
let saveError = ref("");

function onInvalidSubmit(ctx: any) {
  saveError.value = "One or more invalid form inputs.";
  console.log("error trying to submit form", ctx);
}

// Module Edit
let isModulePopupVisible = ref(false);

const { handleSubmit: handleSubmitModule, errors: errorsModule } = useForm({
  validationSchema: yup.object({
    moduleName: yup.string().required().max(100).label("Name"),
    moduleVideoId: yup.mixed<number | string>().notRequired().label("Video"),
  }),
  initialValues: {
    moduleName: "",
    moduleVideoId: 0,
  },
});

const { value: moduleName } = useField<string>("moduleName");
const { value: moduleVideoId } = useField<number | string>("moduleVideoId");
let moduleIsActive = ref(true);
let videoOptions = computed(() =>
  videos.value.map(
    (v) => ({ name: `${v.fileName} (${v.label})`, value: v.videoId } as NameValue)
  )
);
let defaultVideoOption = {
  name: "----None----",
  value: "",
} as NameValue;

let saveModuleClick = handleSubmitModule(onValidSubmitModule, onInvalidSubmit);

async function onValidSubmitModule() {
  let newModule = {
    trainingModuleId: trainingModule.value.trainingModuleId,
    trainingProgramId: trainingModule.value.trainingProgramId,
    portalId: trainingModule.value.portalId,
    name: moduleName.value,
    isActive: moduleIsActive.value,
    videoId: moduleVideoId.value === "" ? undefined : moduleVideoId.value,
  } as TrainingModuleDTO;

  saveError.value = "";

  if (property.value && property.value.portalId) {
    saveError.value = await trainingStore.saveTrainingModule(newModule);
    if (!saveError.value) {
      trainingModule.value.name = moduleName.value;
      trainingModule.value.isActive = moduleIsActive.value;

      if (typeof moduleVideoId.value === "string")
        trainingModule.value.videoId = parseInt(moduleVideoId.value);
      else trainingModule.value.videoId = moduleVideoId.value;
      isModulePopupVisible.value = false;
    }
  }
}

let editModuleClick = function () {
  moduleName.value = trainingModule.value.name;
  moduleIsActive.value = trainingModule.value.isActive;
  moduleVideoId.value = trainingModule.value.videoId;

  saveError.value = "";

  isModulePopupVisible.value = true;
};

// Test Edit
let isTestPopupVisible = ref(false);
let isTestEditing = ref(false);

const { handleSubmit: handleSubmitTest, errors: errorsTest } = useForm({
  validationSchema: yup.object({
    testTitle: yup.string().required().max(50).label("Name"),
  }),
  initialValues: {
    testTitle: "",
  },
});

let testId = ref(0);
const { value: testTitle } = useField<string>("testTitle");
let testIsActive = ref(true);

let saveTestClick = handleSubmitTest(onValidSubmitTest, onInvalidSubmit);

async function onValidSubmitTest() {
  let newTest = {
    testId: testId.value,
    moduleId: trainingModule.value.trainingModuleId,
    title: testTitle.value,
    isActive: testIsActive.value,
  } as TestDTO;

  saveError.value = "";

  if (property.value && property.value.portalId) {
    saveError.value = await trainingStore.saveTest(newTest);
    if (!saveError.value) {
      await trainingStore.getTestsByTrainingModule(trainingModule.value.trainingModuleId);
      isTestPopupVisible.value = false;
    }
  }
}

let addTestClick = function () {
  testId.value = 0;
  testTitle.value = "";
  testIsActive.value = true;
  saveError.value = "";

  isTestEditing.value = false;
  isTestPopupVisible.value = true;
};

let editTestClick = function (selectedTest: TestDTO) {
  testId.value = selectedTest.testId ?? 0;
  testTitle.value = selectedTest.title ?? "";
  testIsActive.value = selectedTest.isActive;

  saveError.value = "";

  isTestEditing.value = true;
  isTestPopupVisible.value = true;
};

async function deleteTestClick() {
  if (testId.value) {
    console.log(`Deleting Test ${testId.value}`);
    saveError.value = await trainingStore.deleteTest(testId.value);
    if (!saveError.value) {
      await trainingStore.getTestsByTrainingModule(trainingModule.value.trainingModuleId);
      isTestPopupVisible.value = false;
    }
  }
}
</script>
